import { stringify } from 'csv-stringify/browser/esm/sync';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';

type CampaignCsvLabels = {
  title: string;
  launchDate: string;
  revenue: string;
  paidOrderCount: string;
  conversionRate: string;
  clicks: string;
  ctr: string;
  notificationTitle: string;
  notificationBody: string;
  notificationLink: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
};

type ExportCampaignsCsvParams = {
  campaigns: CampaignDto[];
  analyticsEnabled: boolean;
  labels: CampaignCsvLabels;
  fileName: string;
};

type CampaignCsvRecord = Record<string, string | number>;

const UTM_SOURCE_VALUE = 'korzaapp';
const UTM_MEDIUM_VALUE = 'push';

export function exportCampaignsCsv({
  campaigns,
  analyticsEnabled,
  labels,
  fileName,
}: ExportCampaignsCsvParams) {
  const columns = [
    { key: 'launchDateTime', header: labels.launchDate },
    { key: 'name', header: labels.title },
    ...(analyticsEnabled
      ? [
          { key: 'revenue', header: labels.revenue },
          { key: 'paidOrderCount', header: labels.paidOrderCount },
          { key: 'conversionRate', header: labels.conversionRate },
          { key: 'clickCount', header: labels.clicks },
          { key: 'ctr', header: labels.ctr },
        ]
      : []),
    { key: 'notificationTitle', header: labels.notificationTitle },
    { key: 'notificationBody', header: labels.notificationBody },
    { key: 'notificationLink', header: labels.notificationLink },
    { key: 'utmSource', header: labels.utmSource },
    { key: 'utmMedium', header: labels.utmMedium },
    { key: 'utmCampaign', header: labels.utmCampaign },
  ];

  const records: CampaignCsvRecord[] = campaigns.map((campaign) => ({
    launchDateTime: campaign.launchDateTime,
    name: campaign.name,
    ...(analyticsEnabled
      ? {
          revenue: campaign.analytics.revenue,
          paidOrderCount: campaign.analytics.paidOrderCount,
          conversionRate: campaign.analytics.conversionRate,
          clickCount: campaign.analytics.clickCount,
          ctr: campaign.analytics.ctr,
        }
      : {}),
    notificationTitle: campaign.notificationTitle,
    notificationBody: campaign.notificationBody,
    notificationLink: campaign.notificationLink,
    utmSource: UTM_SOURCE_VALUE,
    utmMedium: UTM_MEDIUM_VALUE,
    utmCampaign: campaign.utmCampaign,
  }));

  const csv = stringify(records, {
    header: true,
    columns,
  });

  downloadCsv(csv, fileName);
}

function downloadCsv(content: string, fileName: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}
